/**
 * JSON-LD identity for the homepage.
 *
 * A `@graph` of three linked nodes so an agent can resolve the product, the
 * organisation behind it and the site itself from a single script tag:
 *  - SoftwareApplication — what the product is and what it costs
 *  - Organization        — who publishes it, with `sameAs` profiles
 *  - WebSite             — the canonical site entry
 */

import { FEATURES } from "./markdown";
import { absoluteUrl, SITE } from "./site";

const APPLICATION_ID = `${SITE.url}/#software`;
const ORGANIZATION_ID = `${SITE.url}/#organization`;
const WEBSITE_ID = `${SITE.url}/#website`;

export function homepageJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        "@id": APPLICATION_ID,
        "name": SITE.name,
        "alternateName": SITE.fullName,
        "url": absoluteUrl("/"),
        "description": SITE.description,
        "applicationCategory": "MusicApplication",
        "applicationSubCategory": "Chord charts and setlists",
        "operatingSystem": "Web browser, iOS, Android",
        "browserRequirements": "Requires JavaScript and a modern browser.",
        "image": absoluteUrl("/icon-512.png"),
        "featureList": FEATURES.map((feature) => feature.title),
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "url": absoluteUrl("/login"),
        },
        "publisher": { "@id": ORGANIZATION_ID },
        "isPartOf": { "@id": WEBSITE_ID },
      },
      {
        "@type": "Organization",
        "@id": ORGANIZATION_ID,
        "name": SITE.publisher,
        "url": SITE.publisherUrl,
        "logo": absoluteUrl("/icon-512.png"),
        "sameAs": [...SITE.sameAs],
      },
      {
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        "name": SITE.fullName,
        "alternateName": SITE.name,
        "url": absoluteUrl("/"),
        "description": SITE.shortDescription,
        "inLanguage": "en",
        "publisher": { "@id": ORGANIZATION_ID },
      },
    ],
  };
}

/**
 * Serialise for a `<script type="application/ld+json">` tag. `<` is escaped so
 * the payload can never close the surrounding script element.
 */
export function jsonLdScript(data: Record<string, unknown>): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}
